/** FuryBot — Compare Two Firms Flow */
import { track } from "../utils/analytics";
import { formatFirmDisplay } from "@/utils/formatFirmDisplay";
import { getAffiliateLink } from "@/utils/tracking";
import { getSizeNumeric, parseSizeString, findByNumericSize, numToSizeLabel } from "@/utils/sizeUtils";
import { computeTrueCost } from "@/utils/trueCostUtils";
import { estimatePathDays } from "@/utils/pathEstimator";
import { computeFFScore, getBenchmarkTier } from "@/utils/furyScore";

export function createCompareFlowHandlers(ctx) {
  const { firmsRef, firms, addBot, setMessages, ensureFirms, compareFirst, setCompareFirst } = ctx;

  const startCompareFlow = async () => {
    track("flow_started", { flow: "compare" });
    const f = await ensureFirms();
    if (f.length === 0) {
      addBot("I'm pulling the latest firm data. You can wait a moment or check the comparison page:", [
        { label: "Comparison page", action: "link", data: "/futures-prop-firm-comparison" },
        { label: "Try again", action: "flow_compare" },
        { label: "Main menu", action: "home" },
      ]);
      return;
    }
    addBot("Pick the first firm to compare:", f.map(ff => ({ label: ff.name, action: `compare1_${ff.slug}`, data: ff.slug })));
  };

  const handleCompare1 = async (slug) => {
    setCompareFirst(slug);
    const f = await ensureFirms();
    const remaining = f.filter(ff => ff.slug !== slug);
    addBot("Now pick the second firm:", remaining.map(ff => ({ label: ff.name, action: `compare2_${ff.slug}`, data: ff.slug })));
  };

  const handleCompare2 = (slug2) => {
    const f = firmsRef.current.length > 0 ? firmsRef.current : firms;
    const f1 = f.find(ff => ff.slug === compareFirst);
    const f2 = f.find(ff => ff.slug === slug2);
    if (!f1 || !f2) return;
    const bestSize = (() => {
      const sizes1 = new Set((f1.pricing || []).map(p => getSizeNumeric(p)));
      const sizes2 = new Set((f2.pricing || []).map(p => getSizeNumeric(p)));
      for (const s of [150000, 100000, 50000, 25000]) {
        if (sizes1.has(s) && sizes2.has(s)) return s;
      }
      return 50000;
    })();
    addBot("Choose account size to compare:", [
      { label: "50K", action: "compare_size", data: JSON.stringify({ s1: compareFirst, s2: slug2, size: 50000 }) },
      { label: "100K", action: "compare_size", data: JSON.stringify({ s1: compareFirst, s2: slug2, size: 100000 }) },
      { label: "150K", action: "compare_size", data: JSON.stringify({ s1: compareFirst, s2: slug2, size: 150000 }) },
      { label: "Best available", action: "compare_size", data: JSON.stringify({ s1: compareFirst, s2: slug2, size: bestSize }) },
    ]);
  };

  const showCompareResult = (s1, s2, size) => {
    const f = firmsRef.current.length > 0 ? firmsRef.current : firms;
    const f1 = f.find(ff => ff.slug === s1);
    const f2 = f.find(ff => ff.slug === s2);
    if (!f1 || !f2) return;
    const numericSize = typeof size === 'number' ? size : parseSizeString(size);
    const sizeLabel = numToSizeLabel(numericSize);
    track("compare_requested", { firm1: f1.name, firm2: f2.name, size: sizeLabel });

    const getFormattedPricing = (firm, sz) => { const p = findByNumericSize(firm.pricing, sz); const fd = formatFirmDisplay(firm, p); return { pricePrimary: fd.pricePrimary, pricePrimaryRaw: fd.pricePrimaryRaw, priceOriginal: fd.priceOriginal, discountDisplay: fd.discountDisplay, discountValid: fd.discountValid, priceFormatted: fd.priceFormatted }; };
    const getDd = (firm) => formatFirmDisplay(firm).drawdownFull;
    const fp1 = getFormattedPricing(f1, numericSize), fp2 = getFormattedPricing(f2, numericSize);
    const p1 = fp1.pricePrimary || "\u2014", p2 = fp2.pricePrimary || "\u2014";
    const d1 = getDd(f1), d2 = getDd(f2);

    const verdicts = [];
    // Fury Score comparison (SSOT)
    const scoreA = computeFFScore(f1), scoreB = computeFFScore(f2);
    if (scoreA.total !== scoreB.total) {
      const better = scoreA.total > scoreB.total ? f1 : f2;
      const bScore = Math.max(scoreA.total, scoreB.total);
      const wScore = Math.min(scoreA.total, scoreB.total);
      verdicts.push(`${better.name} scores higher overall (Fury ${bScore} vs ${wScore})`);
    }
    const pr1 = fp1.pricePrimaryRaw ?? 0;
    const pr2 = fp2.pricePrimaryRaw ?? 0;
    if (pr1 < pr2) verdicts.push(`${f1.name} is cheaper (${p1} vs ${p2})`);
    else if (pr2 < pr1) verdicts.push(`${f2.name} is cheaper (${p2} vs ${p1})`);
    if (["trailing_eod", "static", "hybrid"].includes(f1.drawdown_type) && f2.drawdown_type === "trailing_intraday") verdicts.push(`${f1.name} has more forgiving drawdown (${d1})`);
    else if (["trailing_eod", "static", "hybrid"].includes(f2.drawdown_type) && f1.drawdown_type === "trailing_intraday") verdicts.push(`${f2.name} has more forgiving drawdown (${d2})`);
    if (f1.profit_split?.includes("100") && !f2.profit_split?.includes("100")) verdicts.push(`${f1.name} offers 100% profit retention`);
    else if (f2.profit_split?.includes("100") && !f1.profit_split?.includes("100")) verdicts.push(`${f2.name} offers 100% profit retention`);

    // True cost comparison (SSOT: computeTrueCost + getBenchmarkTier)
    const tier1 = getBenchmarkTier(f1), tier2 = getBenchmarkTier(f2);
    const tc1 = tier1 ? computeTrueCost(f1, tier1) : null;
    const tc2 = tier2 ? computeTrueCost(f2, tier2) : null;
    if (tc1?.totalCost > 0 && tc2?.totalCost > 0 && Math.abs(tc1.totalCost - tc2.totalCost) >= 30) {
      const prefix1 = tc1.confidence === "exact" ? "$" : "~$";
      const prefix2 = tc2.confidence === "exact" ? "$" : "~$";
      if (tc1.totalCost < tc2.totalCost) verdicts.push(`${f1.name} is cheaper total cost (${prefix1}${tc1.totalCost} vs ${prefix2}${tc2.totalCost})`);
      else verdicts.push(`${f2.name} is cheaper total cost (${prefix2}${tc2.totalCost} vs ${prefix1}${tc1.totalCost})`);
    }

    // Payout speed comparison (SSOT: estimatePathDays)
    const ptp1 = estimatePathDays(f1)?.total ?? 999;
    const ptp2 = estimatePathDays(f2)?.total ?? 999;
    if (ptp1 < 999 && ptp2 < 999 && Math.abs(ptp1 - ptp2) >= 3) {
      if (ptp1 < ptp2) verdicts.push(`${f1.name} pays out faster (~${ptp1} vs ~${ptp2} days)`);
      else verdicts.push(`${f2.name} pays out faster (~${ptp2} vs ~${ptp1} days)`);
    };

    // Rating comparison
    const r1 = f1.verified_trader_rating || f1.rating || 0, r2 = f2.verified_trader_rating || f2.rating || 0;
    if (r1 > 0 && r2 > 0 && Math.abs(r1 - r2) >= 0.3) {
      const better = r1 > r2 ? f1 : f2;
      const bRating = Math.max(r1, r2);
      verdicts.push(`${better.name} rated higher externally (${bRating}/5)`);
    }

    // Consistency rule comparison
    const hasCons1 = f1.consistency_rule && !f1.consistency_rule.toLowerCase().includes("none");
    const hasCons2 = f2.consistency_rule && !f2.consistency_rule.toLowerCase().includes("none");
    if (!hasCons1 && hasCons2) verdicts.push(`${f1.name} has no consistency rule (fewer restrictions)`);
    else if (!hasCons2 && hasCons1) verdicts.push(`${f2.name} has no consistency rule (fewer restrictions)`);

    const compareCard = {
      type: "compare_card",
      firm1: { name: f1.name, slug: f1.slug, price: p1, priceOriginal: fp1.priceOriginal, drawdown: d1, split: formatFirmDisplay(f1).profitSplit, payout: formatFirmDisplay(f1).payoutShort, consistency: f1.consistency_rule || "None", discount: fp1.discountDisplay, affiliate: getAffiliateLink(f1) },
      firm2: { name: f2.name, slug: f2.slug, price: p2, priceOriginal: fp2.priceOriginal, drawdown: d2, split: formatFirmDisplay(f2).profitSplit, payout: formatFirmDisplay(f2).payoutShort, consistency: f2.consistency_rule || "None", discount: fp2.discountDisplay, affiliate: getAffiliateLink(f2) },
      size: sizeLabel,
      verdicts: verdicts.slice(0, 5),
    };

    setMessages(m => [...m, { role: "bot", text: `${f1.name} vs ${f2.name} \u2014 ${sizeLabel} comparison:`, cards: [compareCard], buttons: [
      { label: `${f1.name} profile`, action: "link", data: `/firms/${f1.slug}` },
      { label: `${f2.name} profile`, action: "link", data: `/firms/${f2.slug}` },
      { label: "Show discounts for both", action: "deals_both_compared", data: JSON.stringify({ s1: f1.slug, s2: f2.slug, size: numericSize }) },
      { label: "Compare other firms", action: "flow_compare" },
      { label: "Which is better for me?", action: "flow_match" },
    ], ts: Date.now() }]);
    setCompareFirst(null);
  };

  return { startCompareFlow, handleCompare1, handleCompare2, showCompareResult };
}
